import React, { ChangeEvent, useImperativeHandle, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { FaTimes } from 'react-icons/fa';
import { Editor } from '@tinymce/tinymce-react';
import useAsyncEffect from 'use-async-effect';
import { loading } from '../../actions/Loading';
import api from '../../api/api';
import { Template } from '../../model/Template';
import FormSelectItem from '../form/FormSelectItem';
import AnimatedSaveButton from '../AnimatedSaveButton';
import { SendCampaignRequest } from '../../model/SendCampaignRequest';
import AlertEx from '../AlertEx';
import TextInput from '../form/TextInput';
import { GetCampaignResult } from '../../model/GetCampaignsResult';
import { SelectListItem } from '../../model/SelectListItem';
import { ICampaignPageMethods } from '../stock/emailMarketing/CampaignPage';
import { SaveCampaignRequest, SaveCampaignResult } from '../../model/SaveCampaignRequest';


interface IProps{
    onClose: ()=>void;
    stockId: string;
    campaignId: string;
}

const Style = styled.div`
.editor-container {
  display: flex;
  align-items: stretch;
  gap: 2.5rem;
}

.editor-container > div {
    flex: 1;
    min-width: 0;
}
.editor-container .preview-pane {
    background: #f7f8fa;
    padding: 0.75rem 1rem 1rem;
    border-radius: 6px;
    border: 1px solid #e3e6eb;
}
.editor-container h4 {
    margin: 0 0 0.5rem;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
}
.templates-select{
    min-width:290px;
    max-width:290px;
}
.new-campaign-header{
    display:flex;
    justify-content: space-between;    
}
.new-campaign-actions{
    display:flex;
    margin: 1rem 0;
    align-items: center;
}
.new-campaign-actions > * {
    margin-right: 1rem;
}
.new-campaign-subject{
    max-width: 600px;
}

`;

const EditorContainer = styled.div`
  flex: 60%;
  margin: 1rem 0;
`;


const NewCampaignComponent = React.forwardRef<any, IProps>((props:IProps, ref) => {
    
    const dispatch = useDispatch();
    const [editorContent, setEditorContent] = useState<string>('');
    const [templates, setTemplates] = useState<Template[]>();
    const [templateId, setTemplateId] = useState<string>();
    const [isSending, setIsSending] = useState<boolean>(false);
    const [hasSent, setHasSent] = useState<boolean>(false);
    const [hasSentPreview, setHasSentPreview] = useState<boolean>(false);
    const [savedEditorContent, setSavedEditorContent] = useState<string>();
    const [savedSubject, setSavedSubject] = useState<string>();
    const [subject, setSubject] = useState<string>('');
    const [previewContent, setPreviewContent] = useState<string>('');
    const [numSubscribers, setNumSubscribers] = useState<number>();
    const [campaignId, setCampaignId] = useState<string>(props.campaignId||'');
    const [getCampaignResult, setGetCampaignResult] = useState<GetCampaignResult>();
    const [errorMessage, setErrorMessage] = useState<string>('');
    
    useAsyncEffect(async (isMounted: () => boolean) => {
        
        dispatch(loading(true));
        
        const result = await api().getCampaignTemplates(props.stockId);
        let campaign:GetCampaignResult|undefined;
        if (campaignId && campaignId !== 'new'){
            campaign = await api().getCampaign(campaignId);
            
        }
        
        if (isMounted()) {

            setTemplates(result.templates);
            setNumSubscribers(result.numSubscribers)
            if (campaign) {
                setTemplateId(campaign.templateId)
            } else if (result.templates.length > 0) {
                setTemplateId(result.templates[0].id!)
            }
            setGetCampaignResult(campaign)
        }
        dispatch(loading(false));
        
    }, []);

    useAsyncEffect(async (isMounted: () => boolean) => {
        
        if(templateId){
            onEditorChange('')
        }
        
    }, [ templateId ]);

    useAsyncEffect(async (isMounted: () => boolean) => {
        
        if(getCampaignResult){
            setSubject(getCampaignResult.subject)
            onEditorChange(getCampaignResult.editorContent)
        }
        
    }, [ getCampaignResult ]);

    const getSendCampaignRequest = (isPreview:boolean, campaignId:string):SendCampaignRequest =>{
        return {
                stockId: props.stockId,
                campaignId,
                isPreview: isPreview,
            }
    }
    const getSaveCampaignRequest = (isPreview:boolean):SaveCampaignRequest => {
        return {
                stockId: props.stockId,
                campaignId: campaignId==='new'?'':campaignId,
                templateId: templateId!,
                subject: subject,
                editorContent: editorContent,
                html: previewContent
            }
    }

    useImperativeHandle(ref, (): ICampaignPageMethods => ({

        async save() {
            
           return saveCampaign() != null;
        }

    }));

    const saveCampaign = async ():Promise<SaveCampaignResult|null> => {
        let saveResult = await api().saveCampaign(getSaveCampaignRequest(true));
        if(saveResult.errorMessage){
            AlertEx(saveResult.errorMessage);
            return null;
        }
        setSavedEditorContent(saveResult.editorContent);
        setSavedSubject(saveResult.subject);
        return saveResult;
    }

    const sendCampaign = async () => {
        if (window.confirm(`Send Campaign to your ${numSubscribers} subscribers?\r\n\r\n Are you sure?`)) {
            setIsSending(true)
            const request = getSendCampaignRequest(false, campaignId);
            
            
            let result = await api().sendCampaign(request);
            setIsSending(false)
            if (result.success) {
                setHasSent(true)
                AlertEx('Sent!');
                return true;
            }
            else {
                AlertEx(result.errorMessage);                
            }
            
        }
        return false;
        
    }

        const sendPreview = async () => {
            setIsSending(true)
            let saveResult = await saveCampaign();
            if(!saveResult){
                setIsSending(false);
                return false;
            }
            let result = await api().sendCampaign(getSendCampaignRequest(true, saveResult.campaignId));
            setIsSending(false)
            setCampaignId(saveResult.campaignId)
        if (result.success) {
            setHasSentPreview(true)
            AlertEx(`Preview sent to ${result.previewSentTo}`);
            return true;
        }
        else {
            AlertEx(result.errorMessage);                
        }
        return false;
    }

    const onEditorChange = (val:string)=>{
        onEditorChangeWithState(val, templateId!, templates||[]);
    }
    const onEditorChangeWithState = (val:string, templateId:string, templates:Template[])=>{
       
        setEditorContent(val)
        const template = templates.find(x=>x.id===templateId);
        if(template){            
            setPreviewContent(template.html.replace("{{content_placeholder}}", val))
        }else{
            setErrorMessage(`Unable to find the template with id: '${templateId}'`)
        }
        
    }

    
    let items:SelectListItem[] = [];
    if(templates){
        items = templates.map(x=> ({text:x.name, value: x.id!}));
    }
    
    const sendDisabled = ()=>{
        if (isSending || !hasSentPreview || hasSent || editorContent !== savedEditorContent || subject !== savedSubject ){
            return true;
        }
        return false;
    };

    return <Style>
            <div className="new-campaign-header">
            <div>
                { templateId && templates && templates.length>1 && <FormSelectItem className="templates-select" label="Templates"  value={templateId} 
                            onChange={(e:ChangeEvent<HTMLSelectElement>)=>setTemplateId(e.target.value)} 
                            items={items} />}
            </div>
                <FaTimes className="hand" size={32} color="grey" onClick={props.onClose} />
            </div>

            
               
            

            { templates && templates.length === 0 && <p className='warning'>No templates have been created.</p>}
            { !!errorMessage && <p className='error'>{errorMessage}</p>}
            
            { templateId && !errorMessage && <>
                     <div className='new-campaign-actions'>
                <AnimatedSaveButton onClick={sendPreview} startingText="Send Preview" progressText="Sending..." completedText="Sent" disabled={isSending || hasSent} />
                <AnimatedSaveButton onClick={sendCampaign} startingText={hasSentPreview ? "Send Campaign" : "Send Campaign (must preview first)"} progressText="Sending..." completedText="Sent" disabled={sendDisabled()} />
                <TextInput className='new-campaign-subject' value={subject}  onChange={(e:any)=>setSubject(e.target.value)} disabled={isSending || hasSent} placeholder="Email Subject"  />
           
            </div>
           

            <div className='editor-container'>

                
            <EditorContainer>
                        <Editor
                            disabled={isSending || hasSent}
                            apiKey="fntw2t4q38u7usw5dk4ew34dbgz96zhpezwidg8edbp5jv1k" 
                            value={editorContent}
                            onEditorChange={onEditorChange}
                            init={{
                                onboarding: false,
                                height: 800,
                                menubar: false,
                                //menubar: 'file edit view insert format tools table',
                                plugins: [
                                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                'insertdatetime', 'media', 'paste', 'table', 'help', 'wordcount'
                                ],
                                 setup: function(editor: any) {
                                editor.ui.registry.addButton('mybutton', {
                                text: 'button',
                                onAction: function() {
                                    editor.insertContent('<a href="#" style="display: inline-block; padding: 12px 24px; background-color: #3b82f6; color: white; text-decoration: none; border-radius: 6px; font-weight: 500;">Click Me</a>');
                                }
                                });
                            },
                                toolbar: 'undo redo | blocks | mybutton | bold italic underline code | ' +
                                'forecolor backcolor | alignleft aligncenter alignright alignjustify | ' +
                                'bullist numlist outdent indent | removeformat | ' +
                                'table tabledelete | tableprops tablerowprops tablecellprops | ' +
                                'tableinsertrowbefore tableinsertrowafter tabledeleterow | ' +
                                'tableinsertcolbefore tableinsertcolafter tabledeletecol | ' +
                                'link image media |  fullscreen',
                                toolbar_mode: 'sliding',
                                content_style: 'body { font-family:Arial,sans-serif; font-size:14px }',
                                // Important: Preserve all HTML/CSS
                                valid_elements: '*[*]',
                                extended_valid_elements: '*[*]',
                                valid_children: '+body[style],+div[style]',
                                verify_html: false,
                                cleanup: false,
                                convert_urls: false,
                                remove_trailing_brs: false,
                                entity_encoding: 'raw',
                                paste_preprocess: function(plugin: any, args: any) {
                                const content = args.content;
                                const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
                                const match = content.match(youtubeRegex);
                                
                                if (match) {
                                const videoId = match[1];
                                const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/sddefault.jpg`;
                                const videoUrl = `https://www.youtube.com/watch?v=${videoId}`;
                                
                                args.content = `<a href="${videoUrl}" target="_blank"><img src="${thumbnailUrl}" alt="YouTube Video" style="max-width: 480px;" /></a>`;
                                }
                            }
                            }}
                        />
                    </EditorContainer>
        
                
                {/* Preview */}
                <div className='preview-pane'>
                    <h4>Preview</h4>
                    <iframe
                        srcDoc={previewContent}
                        style={{ 
                            width: '100%',
                            height: '800px',
                            border: '1px solid #ccc'
                        }}
                        title="HTML Preview"
                        sandbox="allow-same-origin"
                    />
                </div>

                </div>
            
            </>}
            

        </Style>
});


export default NewCampaignComponent;